import {
  type GenericMappingByKey,
  GenericMappingType,
} from '../../search/types'
import { getGenericMappingArrayFromGenericMappingByKey } from './utils'

export const INTERCOM_GENERIC_MAPPINGS_BY_KEY = Object.freeze({
  state: {
    type: GenericMappingType.KEYWORD,
    name: 'State',
    description: 'The state of the conversation',
    enum: ['open', 'closed', 'snoozed'],
    order: 0,
  },
  priority: {
    type: GenericMappingType.KEYWORD,
    name: 'Priority',
    description: 'The priority of the conversation',
    enum: ['priority', 'not_priority'],
    order: 1,
  },
  assignee: {
    type: GenericMappingType.KEYWORD,
    name: 'Assignee',
    description: 'The admin assigned to the conversation',
    enumFromCollapseEsQuery: true,
    order: 2,
  },
  team: {
    type: GenericMappingType.KEYWORD,
    name: 'Team',
    description: 'The team assigned to the conversation',
    enumFromCollapseEsQuery: true,
    order: 3,
  },
  tags: {
    type: GenericMappingType.KEYWORD,
    name: 'Tags',
    description: 'The tags associated with the conversation',
    enumFromTermsAggEsQuery: true,
    order: 4,
  },
  creator: {
    type: GenericMappingType.KEYWORD,
    name: 'Creator',
    description: 'The user who created the conversation',
    enumFromCollapseEsQuery: true,
    order: 5,
  },
  rating: {
    type: GenericMappingType.NUMBER,
    name: 'Rating',
    description: 'The rating given to the conversation',
    order: 6,
  },
  waitingSince: {
    type: GenericMappingType.DATE,
    name: 'Waiting Since',
    description:
      'The time since the conversation is waiting for a response (null if last reply is not from the customer)',
    order: 7,
  },
  read: {
    type: GenericMappingType.BOOLEAN,
    name: 'Read',
    description: 'Whether the conversation has been read',
    order: 8,
  },
  source: {
    type: GenericMappingType.KEYWORD,
    name: 'Source',
    description: 'The source of the conversation',
    enum: [
      'conversation',
      'email',
      'facebook',
      'instagram',
      'phone_call',
      'phone_switch',
      'push',
      'sms',
      'twitter',
      'whatsapp',
    ],
    order: 9,
  },
  SLAStatus: {
    type: GenericMappingType.KEYWORD,
    name: 'SLA Status',
    description: 'The SLA status of the conversation',
    enum: ['hit', 'missed', 'cancelled', 'active'],
    order: 10,
  },
  // Statistics
  timeToAssignment: {
    type: GenericMappingType.NUMBER,
    name: 'Time to Assignment',
    description:
      'Duration until last assignment before first admin reply. In seconds.',
    order: 11,
    analytics: true,
  },
  timeToAdminReply: {
    type: GenericMappingType.NUMBER,
    name: 'Time to Admin Reply',
    description:
      'Duration until first admin reply. Subtracts out of business hours. In seconds.',
    order: 12,
    analytics: true,
  },
  timeToFirstClose: {
    type: GenericMappingType.NUMBER,
    name: 'Time to First Close',
    description:
      'Duration until conversation was closed first time. Subtracts out of business hours. In seconds.',
    order: 13,
    analytics: true,
  },
  timeToLastClose: {
    type: GenericMappingType.NUMBER,
    name: 'Time to Last Close',
    description:
      'Duration until conversation was closed last time. Subtracts out of business hours. In seconds.',
    order: 14,
    analytics: true,
  },
  medianTimeToReply: {
    type: GenericMappingType.NUMBER,
    name: 'Median Time to Reply',
    description:
      'Median based on all admin replies after a contact reply. Subtracts out of business hours. In seconds.',
    order: 15,
    analytics: true,
  },
  firstContactReplyAt: {
    type: GenericMappingType.DATE,
    name: 'First Contact Reply At',
    description: 'Time of first text conversation part from a contact.',
    order: 16,
    analytics: true,
  },
  firstAssignmentAt: {
    type: GenericMappingType.DATE,
    name: 'First Assignment At',
    description: 'Time of first assignment after first_contact_reply_at.',
    order: 17,
    analytics: true,
  },
  firstAdminReplyAt: {
    type: GenericMappingType.DATE,
    name: 'First Admin Reply At',
    description: 'Time of first admin reply after first_contact_reply_at.',
    order: 18,
    analytics: true,
  },
  firstCloseAt: {
    type: GenericMappingType.DATE,
    name: 'First Close At',
    description: 'Time of first close after first_contact_reply_at.',
    order: 19,
    analytics: true,
  },
  lastAssignmentAt: {
    type: GenericMappingType.DATE,
    name: 'Last Assignment At',
    description: 'Time of last assignment after first_contact_reply_at.',
    order: 20,
    analytics: true,
  },
  lastAssignmentAdminReplyAt: {
    type: GenericMappingType.DATE,
    name: 'Last Assignment Admin Reply At',
    description: 'Time of first admin reply since most recent assignment.',
    order: 21,
    analytics: true,
  },
  lastContactReplyAt: {
    type: GenericMappingType.DATE,
    name: 'Last Contact Reply At',
    description: 'Time of the last conversation part from a contact.',
    order: 22,
    analytics: true,
  },
  lastAdminReplyAt: {
    type: GenericMappingType.DATE,
    name: 'Last Admin Reply At',
    description: 'Time of the last conversation part from an admin.',
    order: 23,
    analytics: true,
  },
  lastCloseAt: {
    type: GenericMappingType.DATE,
    name: 'Last Close At',
    description: 'Time of the last conversation close.',
    order: 24,
    analytics: true,
  },
  lastClosedBy: {
    type: GenericMappingType.KEYWORD,
    name: 'Last Closed By',
    description: 'The last admin who closed the conversation.',
    enumFromCollapseEsQuery: true,
    order: 25,
    analytics: true,
  },
  countReopens: {
    type: GenericMappingType.NUMBER,
    name: 'Reopen Count',
    description: 'Number of reopens after first_contact_reply_at.',
    order: 26,
    analytics: true,
  },
  countAssignments: {
    type: GenericMappingType.NUMBER,
    name: 'Assignment Count',
    description: 'Number of assignments after first_contact_reply_at.',
    order: 27,
    analytics: true,
  },
  countConversationParts: {
    type: GenericMappingType.NUMBER,
    name: 'Conversation Parts Count',
    description: 'Total number of conversation parts.',
    order: 28,
    analytics: true,
  },
} satisfies GenericMappingByKey)

export const INTERCOM_GENERIC_MAPPINGS =
  getGenericMappingArrayFromGenericMappingByKey(
    INTERCOM_GENERIC_MAPPINGS_BY_KEY
  )
